import { useEffect, useCallback } from 'react';

/**
 * Custom hook to manage body scroll state on desktop (hover-based)
 * Prevents page scroll when hovering over a scrollable element
 * 
 * @param {boolean} enabled - Whether the scroll management is enabled
 * @returns {object} - Event handlers for mouse enter/leave and wheel
 */
export function useScrollManagement(enabled = true) {
  const handleMouseEnter = useCallback(() => {
    if (!enabled) return;
    
    // Only enable hover behavior on non-touch devices
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;
    
    // Disable page scroll when hovering (desktop only)
    document.body.style.overflow = 'hidden';
  }, [enabled]);

  const handleMouseLeave = useCallback(() => {
    if (!enabled) return;
    
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;
    
    // Add a small delay to prevent jumpy scrolling when leaving
    setTimeout(() => {
      document.body.style.overflow = 'auto';
    }, 100);
  }, [enabled]);

  // Cleanup effect to ensure overflow is reset
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  return {
    handleMouseEnter,
    handleMouseLeave,
  };
}

/**
 * Custom hook to handle wheel events within a scrollable container
 * Prevents page scroll when scrolling inside the container
 * 
 * @param {React.RefObject} scrollContainerRef - Reference to the scrollable container
 * @returns {Function} - Wheel event handler
 */
export function useWheelHandler(scrollContainerRef) {
  const handleWheel = useCallback((e) => {
    // Prevent page scroll when scrolling inside container
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
      
      // Only prevent default if we're not at the boundary or scrolling away from boundary
      if ((!isAtTop && e.deltaY < 0) || (!isAtBottom && e.deltaY > 0)) {
        e.stopPropagation();
      }
    }
  }, [scrollContainerRef]);

  return handleWheel;
}

/**
 * Custom hook to reset scroll position to top when a dependency changes
 * Useful for resetting scroll when changing tabs or views
 * 
 * @param {React.RefObject} scrollContainerRef - Reference to the scrollable container
 * @param {any} dependency - Value that triggers scroll reset when it changes
 */
export function useScrollReset(scrollContainerRef, dependency) {
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [scrollContainerRef, dependency]);
}
